import { useMemo } from "react";
import type { CellProps, Column } from "react-table";

import { CopyIconButton } from "@/components/elements/CopyButton/CopyIconButton";
import DefaultTable from "@/components/elements/DefaultTable";
import { ELT_WEBHOOK_API_URL } from "@/constants";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { useCurrentAccount } from "@/providers/account";

import { EltSyncData } from "./ViewDataSource";
import { ButtonGroup } from "@/components/elements/Button/ButtonGroup";

const buildWebhookUrl = (
  accountId: string,
  connectionId: string,
  webhookId: string,
  tableName: string,
) =>
  `${ELT_WEBHOOK_API_URL}/elt-webhooks/${accountId}/${connectionId}/${webhookId}?table_name=${tableName.trim()}`;

type Row = {
  id: string;
  tableName: string;
  url: string;
};

export function WebhookUrlsTable({
  data,
  webhookId,
}: {
  data: EltSyncData;
  webhookId: string;
}) {
  const account = useCurrentAccount();
  const [, copy] = useCopyToClipboard();

  const rows: Row[] = useMemo(
    () =>
      data.sourceStreams.map((stream) => ({
        id: stream.id,
        tableName: stream.name,
        url: buildWebhookUrl(
          account.id,
          data.sourceConnectionId,
          webhookId,
          stream.name,
        ),
      })),
    [account.id, data.sourceConnectionId, data.sourceStreams, webhookId],
  );

  const columns: Column<Row>[] = useMemo(
    () => [
      {
        Header: "Table Name",
        accessor: "tableName",
        width: 30,
        Cell: ({ value }: CellProps<Row, Row["tableName"]>) => (
          <span
            className="block truncate text-left font-mono text-xs"
            title={value}
          >
            {value}
          </span>
        ),
      },
      {
        Header: "Webhook URL",
        accessor: "url",
        Cell: ({ value }: CellProps<Row, Row["url"]>) => (
          <div className="flex min-w-0 items-center gap-2">
            <span
              className="min-w-0 flex-1 truncate text-left font-mono text-xs text-muted-foreground"
              title={value}
            >
              {value}
            </span>
            {!!value && (
              <ButtonGroup size="sm">
                <CopyIconButton onClick={() => copy(value)} />
              </ButtonGroup>
            )}
          </div>
        ),
      },
    ],
    [copy],
  );

  return (
    <div className="max-h-[35vh] w-full overflow-y-auto">
      <DefaultTable
        columns={columns}
        data={rows}
        isSortable={false}
        tableClassName="table-fixed"
      />
    </div>
  );
}
